import { useState, useMemo } from "react";
import React from 'react';
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import "../../App.css";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { Row, Col, Button } from "reactstrap";
import { default as ReactSelect } from "react-select";
import { getEquipmentReport } from "../../services/ReportServices";
import { DatePicker } from 'antd';
import { getAllPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getAllCustomerSites } from '../../services/CustomerSiteServices';
import { getAllProducts } from "../../services/ProductServices";
import { getAllEquipments } from '../../services/EquipmentServices';
import moment from 'moment';
import { displayError } from '../../helpers';
import { HandleSelect, ValueContainer, Option } from "../../components/MultiSelectControls";
import ReportExportDropdown from '../../components/ReportExportDropdown';

const { RangePicker } = DatePicker;

function EquipmentReportTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [plants, setPlants] = React.useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSalesrep, setSelectedSalesRep] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedSite, setSelectedSite] = useState([]);
    const [sites, setSites] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState([]);
    const [grades, setGrades] = useState([]);
    const [inputs, setInputs] = useState({ report_format: 'date' });

    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    const handleDateChange = (values) => {
        if (values && values.length === 2) {
            setFromDate(values[0]);
            setToDate(values[1]);
        }
    };

    React.useEffect(() => {
        getAllPlants(cookies)
            .then(plantList => {
                const plantOptions = plantList.plant_list
                    .filter(obj => obj.status)
                    .map(plant => ({ value: plant.id, label: plant.plant_alias }));
                setPlants(plantOptions);
                setSelectedPlant(values => [...values, ...plantOptions, ...plantsWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllCustomerMain(cookies)
            .then(customerList => {
                const customerOptions = customerList.customer_list
                    .filter(obj => obj.status)
                    .map(customer => ({ value: customer.id, label: customer.name }));
                setCustomers(customerOptions);
                setSelectedCustomer(values => [...values, ...customerOptions, ...customersWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllCustomerSites(cookies)
            .then(siteList => {
                const siteOptions = siteList.consignee_list
                    .filter(obj => obj.status)
                    .map(site => ({ value: site.id, label: site.name }));
                setSites(siteOptions);
                setSelectedSite(values => [...values, ...siteOptions, ...sitesWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllSalesRepresentatives(cookies)
            .then(salesRepList => {
                const salesrepOptions = salesRepList.salesrep_data
                    .filter(obj => obj.status)
                    .map(sales => ({ value: sales.id, label: sales.entity_name }));
                setSalesreps(salesrepOptions);
                setSelectedSalesRep(values => [...values, ...salesrepOptions, ...salesrepsWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllEquipments(cookies)
            .then(equipmentObject => {
                const equipmentOptions = equipmentObject.equipment_list
                    .filter(obj => obj.status)
                    .map(equipment => ({ value: equipment.id, label: equipment.equip_name }));
                setVehicles(equipmentOptions);
                setSelectedVehicle(values => [...values, ...equipmentOptions, ...vehiclesWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllProducts(cookies)
            .then(productList => {
                const productOptions = productList.product_list
                    .filter(obj => obj.status)
                    .map(product => ({ value: product.id, label: product.name }));
                setGrades(productOptions);
                setSelectedGrade(values => [...values, ...productOptions, ...gradesWithSelectAll]);
            });
    }, []);

    const plantsWithSelectAll = [
        { value: "select-all", label: "Select All plants" },
        ...plants,
    ];

    const customersWithSelectAll = [
        { value: "select-all", label: "Select All customers" },
        ...customers,
    ];

    const sitesWithSelectAll = [
        { value: "select-all", label: "Select All sites" },
        ...sites,
    ];

    const salesrepsWithSelectAll = [
        { value: "select-all", label: "Select All sales representatives" },
        ...salesreps,
    ];

    const vehiclesWithSelectAll = [
        { value: "select-all", label: "Select All vehicles" },
        ...vehicles,
    ];

    const gradesWithSelectAll = [
        { value: "select-all", label: "Select All grades" },
        ...grades,
    ];

    const onPlantSelect = (selected) => {
        HandleSelect(selected, selectedPlant, setSelectedPlant, plants, plantsWithSelectAll);
    };

    const onCustomerSelect = (selected) => {
        HandleSelect(selected, selectedCustomer, setSelectedCustomer, customers, customersWithSelectAll);
    };

    const onSiteSelect = (selected) => {
        HandleSelect(selected, selectedSite, setSelectedSite, sites, sitesWithSelectAll);
    };

    const onSalesRepSelect = (selected) => {
        HandleSelect(selected, selectedSalesrep, setSelectedSalesRep, salesreps, salesrepsWithSelectAll);
    };

    const onVehicleSelect = (selected) => {
        HandleSelect(selected, selectedVehicle, setSelectedVehicle, vehicles, vehiclesWithSelectAll);
    };

    const onGradeSelect = (selected) => {
        HandleSelect(selected, selectedGrade, setSelectedGrade, grades, gradesWithSelectAll);
    };

    const areAllPlantsSelected = () => plants.every((plant) =>
        selectedPlant ? selectedPlant.some((selected) => selected.value === plant.value) : false
    );

    const areAllCustomersSelected = () => customers.every((customer) =>
        selectedCustomer ? selectedCustomer.some((selected) => selected.value === customer.value) : false
    );

    const areAllConsigneesSelected = () => sites.every((consignee) =>
        selectedSite ? selectedSite.some((selected) => selected.value === consignee.value) : false
    );

    const areAllSalesRepsSelected = () => salesreps.every((salesrep) =>
        selectedSalesrep ? selectedSalesrep.some((selected) => selected.value === salesrep.value) : false
    );

    const areAllVehiclesSelected = () => vehicles.every((vehicle) =>
        selectedVehicle ? selectedVehicle.some((selected) => selected.value === vehicle.value) : false
    );

    const areAllGradesSelected = () => grades.every((grade) =>
        selectedGrade ? selectedGrade.some((selected) => selected.value === grade.value) : false
    );

    const handleChange = (event) => {
        const name = event.target.name;
        setInputs(values => ({ ...values, [name]: event.target.value }));
    };

    const columns = useMemo(
        () => [
            { fieldName: 'entity_name', headerName: 'Plant Alias' },
            { fieldName: 'entity_name', headerName: 'Production Plant' },
            { fieldName: "address", headerName: 'Date' },
            { fieldName: 'entity_name', headerName: 'DC Number' },
            { fieldName: "mobile_number", headerName: 'Batch Number' },
            { fieldName: 'entity_name', headerName: 'Time' },
            { fieldName: "address", headerName: 'SO Number' },
            { fieldName: "address", headerName: 'PO Number' },
            { fieldName: "address", headerName: 'Invoice Number' },
            { fieldName: 'entity_name', headerName: 'Customer Name' },
            { fieldName: "address", headerName: 'Site/Project Name' },
            { fieldName: "address", headerName: 'Grade' },
            { fieldName: 'entity_name', headerName: 'Quantity' },
            { fieldName: 'entity_name', headerName: 'Rate' },
            { fieldName: 'entity_name', headerName: 'Mode of Delivery' },
            { fieldName: 'entity_name', headerName: 'Vehicle No' },
            { fieldName: "address", headerName: 'Marketting' },
            { fieldName: "address", headerName: 'Driver name' },
            { fieldName: "address", headerName: 'Open KM' },
            { fieldName: "address", headerName: 'End KM' },
            { fieldName: 'entity_name', headerName: 'DC Grade' },
            { fieldName: 'entity_name', headerName: 'Total Km' },
            { fieldName: 'entity_name', headerName: 'Tare weight' },
            { fieldName: 'entity_name', headerName: 'Gross weight' },
            { fieldName: 'entity_name', headerName: 'Net weight' },
            { fieldName: "address", headerName: 'Dc Status' },
        ],
        [],
    );

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(c => c.headerName),
            data.records.map(r => ({
                entity_name: r.entity_name,
                address: r.address,
                phone_number: r.phone_number,
                mobile_number: r.mobile_number,
                status: r.status,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'Equipment Report',
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                entity_name: r.entity_name,
                address: r.address,
                phone_number: r.phone_number,
                mobile_number: r.mobile_number,
                status: r.status,
            })),
            'Equipment.pdf'
        );
    };

    const constructFormData = () => ({
        plant_ids: areAllPlantsSelected() ? '*'
            : selectedPlant.map((plant) => plant.value).join(','),
        customer_ids: areAllCustomersSelected() ? '*'
            : selectedCustomer.map((customer) => customer.value).join(','),
        consignee_ids: areAllConsigneesSelected() ? '*'
            : selectedSite.map((consignee) => consignee.value).join(','),
        sales_rep_ids: areAllSalesRepsSelected() ? '*'
            : selectedSalesrep.map((salesrep) => salesrep.value).join(','),
        vehicle_ids: areAllVehiclesSelected() ? '*'
            : selectedVehicle.map((vehicle) => vehicle.value).join(','),
        product_ids: areAllGradesSelected() ? '*'
            : selectedGrade.map((grade) => grade.value).join(','),
        from_date: fromDate.format('DD-MM-YYYY'),
        upto_date: toDate.format('DD-MM-YYYY'),
        report_format: inputs.report_format,
    });

    const handleGenerate = () => {
        const formData = constructFormData();
        if ((formData.plant_ids) && (formData.customer_ids) && (formData.consignee_ids)
            && (formData.sales_rep_ids) && (formData.vehicle_ids) && (formData.product_ids)
            && (formData.from_date) && (formData.upto_date) && (formData.report_format))
        {
            setIsLoading(true);
            getEquipmentReport(cookies, formData)
                .then(response => {
                    const rows = response.salesrep_data || response.equipment_report_list || [];
                    const tableData = rows.map(row => ({
                        entity_name: row.entity_name,
                        address: row.address_1
                            ? `${row.address_1},\n${row.address_2 || ''},\n${row.address_3 || ''}`
                            : row.address,
                        phone_number: row.phone_number,
                        mobile_number: row.mobile_number,
                        status: row.status,
                        id: row.id,
                    }));
                    setData({
                        total: 0,
                        records: tableData,
                    });
                    setIsLoading(false);
                })
                .catch((error) => {
                    displayError(error.response?.data || error);
                    setIsLoading(false);
                });
        }
        else {
            Swal.fire("plant,customer,site,sales rep,vehicle,grade,from date,upto date,report format must not be empty.", "", "info");
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        handleGenerate();
    };

    return (
        <>
            <form className="form" onSubmit={handleSubmit}>
                <div className="container">
                    <Row>
                        <Col xl={12} lg={12} md={12}>
                            <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <div className="report-legacy-filters-fields">
                                    <ReactSelect
                                        options={plantsWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedPlant}
                                        onChange={onPlantSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Plant"
                                    />
                                    <ReactSelect
                                        options={customersWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedCustomer}
                                        onChange={onCustomerSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Customer"
                                    />
                                    <ReactSelect
                                        options={sitesWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedSite}
                                        onChange={onSiteSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Site"
                                    />
                                    <ReactSelect
                                        options={salesrepsWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedSalesrep}
                                        onChange={onSalesRepSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select SalesRep"
                                    />
                                    <ReactSelect
                                        options={vehiclesWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedVehicle}
                                        onChange={onVehicleSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Vehicle"
                                    />
                                    <ReactSelect
                                        options={gradesWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedGrade}
                                        onChange={onGradeSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Grade"
                                    />
                                </div>
                                <div className="report-legacy-filters-row">
                                    <RangePicker
                                        defaultValue={[initialFromDate, initialToDate]}
                                        format="DD-MM-YYYY"
                                        onChange={handleDateChange}
                                    />
                                    <select
                                        className="browser-default custom-select col-sm-3"
                                        onChange={handleChange}
                                        value={inputs.report_format || 'date'}
                                        name="report_format"
                                    >
                                        <option value="date">Datewise</option>
                                        <option value="product">Product</option>
                                        <option value="vendor">Supplier</option>
                                        <option value="vehicle">Vehicle</option>
                                    </select>
                                    <div className="report-legacy-actions">
                                        <ReportExportDropdown
                                            onCSV={handleCSVExport}
                                            onPDF={handlePDFExport}
                                            disabled={isLoading}
                                        />
                                        <Button
                                            color="primary"
                                            className="list-page-toolbar-generate-btn"
                                            type="button"
                                            onClick={handleGenerate}
                                            disabled={isLoading}
                                        >
                                            Generate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </form>
            <div className="container item-list-table-container">
                <div className="table-responsive">
                    <ReactTableWrapper
                        title="Equipment Report"
                        columns={columns}
                        data={data.records}
                        onCSVExport={handleCSVExport}
                        onPDFExport={handlePDFExport}
                        isLoading={isLoading}
                        enableTableSearch={false}
                    />
                </div>
            </div>
        </>
    );
}

export default EquipmentReportTable;
